<template>
  <component :is="resolvedComponent" v-model="localValue" v-bind="resolvedProps" />
</template>

<script setup>
import { computed, defineEmits, defineProps, useAttrs } from 'vue'
import InputCep from '@/components/lvm/molecules/InputCep.vue'
import InputCheck from '@/components/lvm/molecules/InputCheck.vue'
import InputDate from '@/components/lvm/molecules/InputDate.vue'
import InputDateRange from '@/components/lvm/molecules/InputDateRange.vue'
import InputDateTime from '@/components/lvm/molecules/InputDateTime.vue'
import InputDocument from '@/components/lvm/molecules/InputDocument.vue'
import InputEmail from '@/components/lvm/molecules/InputEmail.vue'
import InputFile from '@/components/lvm/molecules/InputFile.vue'
import InputHour from '@/components/lvm/molecules/InputHour.vue'
import InputImage from '@/components/lvm/molecules/InputImage.vue'
import InputMoney from '@/components/lvm/molecules/InputMoney.vue'
import InputMonth from '@/components/lvm/molecules/InputMonth.vue'
import InputNumber from '@/components/lvm/molecules/InputNumber.vue'
import InputPassword from '@/components/lvm/molecules/InputPassword.vue'
import InputPhone from '@/components/lvm/molecules/InputPhone.vue'
import InputText from '@/components/lvm/molecules/InputText.vue'
import InputTime from '@/components/lvm/molecules/InputTime.vue'
import InputZoom from '@/components/lvm/molecules/InputZoom.vue'
import InputZoomMultiple from '@/components/lvm/molecules/InputZoomMultiple.vue'
import Select from '@/components/lvm/molecules/Select.vue'
import SelectMultiple from '@/components/lvm/molecules/SelectMultiple.vue'
import SelectTable from '@/components/lvm/molecules/SelectTable.vue'
import TextArea from '@/components/lvm/molecules/TextArea.vue'

const props = defineProps({
  modelValue: {
    type: [String, Number, Boolean, Array, Object, Date, File],
    default: null,
  },
  type: {
    type: String,
    default: 'text',
  },
  label: String,
  status: String,
  placeholder: String,
  required: Boolean,
  disabled: Boolean,
  error: {
    type: [Array, String, Object],
    default: null,
  },
  options: {
    type: Array,
    default: () => [],
  },
  items: {
    type: Array,
    default: null,
  },
  database: String,
  itemValue: {
    type: String,
    default: 'value',
  },
  itemText: {
    type: String,
    default: 'label',
  },
  itemAutocomplete: [String, Array],
  tableFilter: String,
  clearable: Boolean,
  mask: String,
  min: [String, Number],
  max: [String, Number],
  dense: Boolean,
  integer: Boolean,
  rows: [String, Number],
  minlength: [String, Number],
  maxlength: [String, Number],
})

const emit = defineEmits(['update:modelValue'])
const attrs = useAttrs()

const localValue = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
})

const typeAliases = {
  textarea: 'textarea',
  textArea: 'textarea',
  number: 'number',
  money: 'money',
  date: 'date',
  range: 'range',
  dateRange: 'range',
  daterange: 'range',
  datetime: 'datetime',
  dateTime: 'datetime',
  monthly: 'month',
  month: 'month',
  time: 'time',
  hour: 'hour',
  email: 'email',
  password: 'password',
  phone: 'phone',
  cep: 'cep',
  document: 'document',
  file: 'file',
  image: 'image',
  check: 'check',
  checkbox: 'check',
  bool: 'check',
  boolean: 'check',
  select: 'select',
  selectMultiple: 'selectMultiple',
  multiselect: 'selectMultiple',
  selectTable: 'selectTable',
  zoom: 'zoom',
  zoomMultiple: 'zoomMultiple',
  zoommultiple: 'zoomMultiple',
}

const componentByType = {
  text: InputText,
  textarea: TextArea,
  number: InputNumber,
  money: InputMoney,
  date: InputDate,
  range: InputDateRange,
  datetime: InputDateTime,
  month: InputMonth,
  time: InputTime,
  hour: InputHour,
  email: InputEmail,
  password: InputPassword,
  phone: InputPhone,
  cep: InputCep,
  document: InputDocument,
  file: InputFile,
  image: InputImage,
  check: InputCheck,
  select: Select,
  selectMultiple: SelectMultiple,
  selectTable: SelectTable,
  zoom: InputZoom,
  zoomMultiple: InputZoomMultiple,
}

const normalizedType = computed(() => {
  const raw = props.type || 'text'
  return typeAliases[raw] ?? raw
})

const resolvedComponent = computed(() => componentByType[normalizedType.value] ?? InputText)

const resolvedItems = computed(() => props.items ?? props.options ?? [])

const resolvedProps = computed(() => ({
  ...attrs,
  label: props.label,
  status: props.status,
  placeholder: props.placeholder,
  required: props.required,
  disabled: props.disabled,
  error: props.error,
  clearable: props.clearable,
  database: props.database,
  items: resolvedItems.value,
  itemValue: props.itemValue,
  itemText: props.itemText,
  itemAutocomplete: props.itemAutocomplete,
  tableFilter: props.tableFilter,
  mask: props.mask,
  min: props.min,
  max: props.max,
  dense: props.dense,
  integer: props.integer,
  rows: props.rows,
  minlength: props.minlength,
  maxlength: props.maxlength,
}))
</script>

